import { el, iconBtn, type ToolId } from '../panels.js';
import { canDelete, canEdit } from '../commands.js';
import { observeSize } from '../../layers.js';
import { fillText, isBlocking, problemMessage } from '../texts.js';
import type { CreateShell, DesignerShell, ShellOptions } from '../shell-types.js';
import { openAddDrawer, tabForTool } from './add-drawer.js';
import { openContextSheet } from './context-sheet.js';
import { phoneIconSvg } from './icons.js';
import { openMoreSheet, openSidesSheet } from './more-menu.js';
import { createPropertyGrid, gridKindOf, phoneGridCaps } from './property-grid.js';
import { canHaveFixedWidth, isFixedTextBox } from '../text-box.js';
import { openReviewSheet } from './review-sheet.js';
import { openSheet, type SheetHandle } from './sheet.js';
import { nextPhoneStep, type DesignerStep } from '../steps.js';
import { runGridItem } from './sub-panels.js';
import { createArrangeStrip } from '../arrange-strip.js';
import { phoneTexts } from './texts-phone.js';
import type { PhoneEnv, PhoneTabId, SubPanelSpec } from './types-phone.js';

/**
 * The phone shell — Zazzle's mobile web design tool, on our engine.
 *
 * Top bar (undo · redo · ⋯ · GOTOVO), the shared canvas, and a bottom area
 * that is either the property grid (four cells per row, label above icon) or
 * one open "OTKAŽI | Naslov | PRIMENI" sub-panel. Everything else — the Add
 * drawer, the ⋯ menu, Pregled, the long-press menu — is a bottom sheet over
 * the whole thing.
 *
 * What this file owns is the chrome and the wiring; the grid's lists live in
 * property-grid.ts, the panels in sub-panels.ts, the sheets in their own
 * files. The engine (store, commands, canvas, gestures, validation) is shared
 * with the desktop shell untouched: this is a second face, not a second
 * designer.
 *
 * Two things are worth knowing before changing anything here.
 *
 * The shell subscribes to the store itself instead of waiting to be told. The
 * engine does call `refresh`/`sync`, but the top bar and the grid must also be
 * right after a canvas drag, an undo from the keyboard or a command run from a
 * sheet — and a subscription is cheaper to reason about than a list of call
 * sites. The grid is rebuilt only when its SIGNATURE changed, so dragging a
 * layer does not rebuild fifteen buttons sixty times a second.
 *
 * The long press is ours, not the canvas's. The canvas's own 520 ms press
 * opens a layer's tool (the desktop double-click gesture); a phone expects a
 * context menu. So the shell watches the same pointer at 470 ms, opens the
 * context sheet, and suppresses the canvas's `open(tool)` that follows.
 */

/** Long-press delay; deliberately under the canvas's own 520 ms. */
const PRESS_MS = 470;
/** How far a finger may travel before it is a drag, not a press. */
const PRESS_SLOP = 8;
/** After a long press, ignore the canvas's own "open this tool" for this long. */
const SUPPRESS_MS = 1200;
/** Above this width the grid shows five per row (a tablet in portrait). */
const WIDE_AT = 700;
/** Below this height a landscape phone puts the grid beside the canvas. */
const SHORT_AT = 520;
/** …but only once there is room for a column beside the canvas. */
const LANDSCAPE_FROM = 560;
/** Under this height the bottom area gives the canvas a bigger share. */
const SHORT_SHELL = 620;
/** Below this width the side name takes a row of its own. */
const SIDE_ROW_BELOW = 420;
/** A visual viewport this much shorter than the layout viewport means a keyboard. */
const KEYBOARD_AT = 140;

export const createPhoneShell: CreateShell = (options: ShellOptions): DesignerShell => {
  const ctx = options.ctx;
  const actions = options.actions;
  const texts = phoneTexts(ctx.texts);

  const root = el('div', 'pzd-shell is-phone');
  let destroyed = false;
  // The engine's own sheet keys several rules (16 px inputs, coarse hit
  // tolerance on the canvas) off this class on the designer root.
  ctx.root.classList.add('is-phone');

  /**
   * Two pieces of desktop furniture the phone does without, and Zazzle's
   * mobile tool does not draw either: the rulers (a 375 px screen has no room
   * to spend 18 px on a scale) and the floating mini toolbar (the property
   * grid IS the toolbar here, and the pill lands on top of the very object it
   * describes). They are the shopper's settings, so whatever they were is put
   * back when the shell is destroyed — a window dragged past 860 px must not
   * cost a desktop user their rulers.
   */
  const restoreView = { rulers: ctx.view.rulers, miniToolbar: ctx.view.miniToolbar };
  if (ctx.view.rulers || ctx.view.miniToolbar) ctx.setView({ rulers: false, miniToolbar: false });

  // ── Top bar ────────────────────────────────────────────────────────────────
  const top = el('div', 'pzd-ph-top');
  const topMain = el('div', 'pzd-ph-top-main');
  // On a narrow phone the side name gets a line of its own rather than an
  // ellipsis: "Prednja..." is not a side of a product.
  const sideRow = el('div', 'pzd-ph-side-row');
  sideRow.hidden = true;
  const topLeft = el('div', 'pzd-ph-top-left');
  const topMid = el('div', 'pzd-ph-top-mid');
  const topRight = el('div', 'pzd-ph-top-right');
  const undoBtn = iconBtn('undo', texts.undo, () => { closeSub(false); actions.undo(); }, { className: 'pzd-ph-icon pzd-ph-undo' });
  const redoBtn = iconBtn('redo', texts.redo, () => { closeSub(false); actions.redo(); }, { className: 'pzd-ph-icon pzd-ph-redo' });
  topLeft.append(undoBtn, redoBtn);

  const sideBtn = el('button', 'pzd-ph-side');
  sideBtn.type = 'button';
  const sideName = el('span');
  sideBtn.append(sideName);
  const chevron = el('span');
  chevron.innerHTML = phoneIconSvg('chevron', 16);
  sideBtn.append(chevron);
  sideBtn.setAttribute('aria-label', texts.sidePick);
  sideBtn.addEventListener('click', () => env.openSides());

  const savedTag = el('span', 'pzd-ph-saved', texts.saved);
  const problemChip = el('button', 'pzd-ph-chip is-problem');
  problemChip.type = 'button';
  problemChip.hidden = true;
  problemChip.addEventListener('click', () => {
    if (!actions.explainFirstProblem()) return;
    closeSheet();
  });
  const surfaces = options.surfaces ?? [];
  topMid.append(surfaces.length > 1 ? sideBtn : savedTag, problemChip);

  // The surcharge is a caption under GOTOVO rather than a chip in the middle:
  // the top bar has four things in it already, and the number belongs beside
  // the button that accepts it. The Pregled sheet repeats it in full.
  const priceCap = el('span', 'pzd-ph-price-cap');
  priceCap.hidden = true;

  const moreBtn = el('button', 'pzd-ph-icon pzd-ph-more');
  moreBtn.type = 'button';
  moreBtn.innerHTML = phoneIconSvg('dots', 20);
  moreBtn.setAttribute('aria-label', texts.more);
  moreBtn.addEventListener('click', () => env.openMore());
  const doneBtn = el('button', 'pzd-ph-done', texts.done);
  doneBtn.type = 'button';
  doneBtn.setAttribute('aria-label', texts.done);
  // Zazzle's guided flow on a phone: GOTOVO reads DALJE while there is another
  // side to design, then the product options, and only then finishes.
  const productOptions = options.productOptions;
  let optionsSeen = false;
  const phoneStep = (): DesignerStep => nextPhoneStep(surfaces, Boolean(productOptions), optionsSeen);
  doneBtn.addEventListener('click', () => {
    closeSub(false);
    const step = phoneStep();
    if (step.kind === 'surface') options.switchSurface?.(step.id);
    else if (step.kind === 'options') env.openOptions?.();
    else actions.done();
  });
  const doneBox = el('div', 'pzd-ph-done-box');
  doneBox.append(doneBtn, priceCap);
  topRight.append(moreBtn, doneBox);
  topMain.append(topLeft, topMid, topRight);
  top.append(topMain, sideRow);

  // ── Stage ──────────────────────────────────────────────────────────────────
  const stage = el('div', 'pzd-ph-stage');
  stage.append(options.canvas.element);
  const safePill = el('div', 'pzd-ph-safe-pill', ctx.texts.safeArea);
  safePill.hidden = true;
  stage.append(safePill);

  // ── Bottom area ────────────────────────────────────────────────────────────
  const bottom = el('div', 'pzd-ph-bottom');
  const grid = createPropertyGrid({
    texts,
    onItem: (item) => runGridItem(env, item),
  });
  // Rasporedi, above the grid: only while nothing is selected and no panel is
  // open — editing one thing needs the room, choosing a layout does not.
  const arrange = createArrangeStrip(ctx, { compact: true, onToggle: () => window.setTimeout(() => options.canvas.relayout(), 0) });
  bottom.append(arrange.element, grid.element);

  root.append(top, stage, bottom);

  // ── What is open right now ─────────────────────────────────────────────────
  let sheet: SheetHandle | null = null;
  let sub: { spec: SubPanelSpec; wrap: HTMLElement; teardown?: () => void } | null = null;
  let openTool: ToolId | null = null;
  let suppressOpenUntil = 0;
  let signature = '';
  let lastSize = '';

  function closeSheet(): void {
    const open = sheet;
    sheet = null;
    openTool = null;
    open?.close();
  }

  // The snapshot belongs to the open panel; it is kept beside the spec rather
  // than inside it so a spec stays a plain description.
  const snapshots = new WeakMap<SubPanelSpec, ReturnType<typeof ctx.store.state>>();
  function snapshotOf(spec: SubPanelSpec): ReturnType<typeof ctx.store.state> {
    return snapshots.get(spec) ?? ctx.store.state();
  }

  /**
   * Closes the open sub-panel. `restore` puts the design back as it was when
   * the panel opened — see `restorePlan` for why that is sometimes an undo and
   * sometimes a plain write.
   */
  function closeSub(restore: boolean): void {
    const open: { spec: SubPanelSpec; wrap: HTMLElement; teardown?: () => void } | null = sub;
    if (!open) return;
    sub = null;
    // Tear down controls before restoring history: pending live values must
    // never write into the restored design.
    open.teardown?.();
    open.wrap.remove();
    ctx.dispatch({ type: 'editSession', snapshot: snapshotOf(open.spec), cancel: restore });
    if (restore) open.spec.cancel?.();
    grid.element.hidden = false;
    refreshGrid();
  }

  function showSub(spec: SubPanelSpec | null): void {
    closeSub(false);
    if (!spec) return;
    snapshots.set(spec, ctx.store.state());
    const wrap = el('div', `pzd-ph-sub is-${spec.id}`);
    const head = el('div', 'pzd-ph-sub-head');
    const cancel = el('button', 'pzd-ph-text-btn is-quiet', texts.cancel);
    cancel.type = 'button';
    cancel.setAttribute('aria-label', texts.cancel);
    cancel.addEventListener('click', () => closeSub(true));
    const heading = el('h3', undefined, spec.title);
    const apply = el('button', 'pzd-ph-text-btn', texts.apply);
    apply.type = 'button';
    apply.setAttribute('aria-label', texts.apply);
    apply.addEventListener('click', () => {
      spec.apply?.();
      closeSub(false);
    });
    head.append(cancel, heading, apply);
    const body = el('div', 'pzd-ph-sub-body');
    wrap.append(head, body);
    grid.element.hidden = true;
    bottom.append(wrap);
    sub = { spec, wrap };
    const teardown = spec.build(body);
    sub = { spec, wrap, ...(typeof teardown === 'function' ? { teardown } : {}) };
    refreshGrid();
    body.querySelector<HTMLElement>('input, textarea, button, [tabindex]')?.focus({ preventScroll: true });
  }

  // ── Painting ───────────────────────────────────────────────────────────────

  /** What the grid depends on; rebuilt only when this string changes. */
  function gridSignature(): string {
    const layer = ctx.selected();
    if (!layer) return `none|${sub?.spec.id ?? ''}`;
    const authored = `${layer.permanent === true}/${layer.interactive === false}`;
    const swatch = layer.kind === 'text'
      ? layer.color?.hex ?? ''
      : layer.kind === 'shape'
        ? `${layer.fill?.hex ?? ''}/${layer.stroke?.color.hex ?? ''}`
        : layer.kind === 'artwork' ? layer.singleColor?.hex ?? '' : '';
    const orientation = layer.kind === 'text' ? layer.orientation ?? 'horizontal' : '';
    // Bending a line takes its wrap column away, so the grid has one cell fewer.
    const wrappable = layer.kind === 'text'
      ? `${canHaveFixedWidth(layer)}/${isFixedTextBox(layer)}`
      : '';
    return [layer.id, layer.kind, orientation, wrappable, swatch, authored, sub?.spec.id ?? ''].join('|');
  }

  function refreshGrid(): void {
    signature = gridSignature();
    const layer = ctx.selected();
    arrange.setSuppressed(Boolean(layer) || Boolean(sub));
    grid.render(
      {
        kind: gridKindOf(layer),
        ...(layer?.kind === 'text'
          ? {
            vertical: layer.orientation === 'vertical',
            wrappable: canHaveFixedWidth(layer),
            fixedBox: isFixedTextBox(layer),
          }
          : {}),
        // What the shop authored: a permanent layer keeps no Obriši, and a
        // non-interactive one offers nothing at all (`commands.ts` decides).
        editable: canEdit(layer),
        deletable: canDelete(layer),
        caps: phoneGridCaps(ctx.config, {
          removeBackground: Boolean(ctx.uploads.removeBackground),
          templates: options.defs.some((def) => def.id === 'templates' && def.allowed),
        }),
      },
      layer,
      sub?.spec.id ?? null,
    );
  }

  function syncTop(): void {
    undoBtn.disabled = !actions.canUndo();
    redoBtn.disabled = !actions.canRedo();
    const price = actions.price();
    priceCap.hidden = !price;
    if (price) {
      priceCap.textContent = price;
      priceCap.setAttribute('aria-label', fillText(ctx.texts.price, { price }));
    }
    const blocking = actions.problems().filter(isBlocking);
    problemChip.hidden = blocking.length === 0;
    const first = blocking[0];
    if (first) {
      problemChip.textContent = String(blocking.length);
      problemChip.setAttribute('aria-label', `${texts.problems}: ${problemMessage(first)}`);
      problemChip.title = problemMessage(first);
    }
    const current = surfaces.find((surface) => surface.current);
    if (current) {
      sideName.textContent = current.name;
      sideBtn.setAttribute('aria-label', `${texts.sidePick}: ${current.name}`);
    }
    const step = phoneStep();
    doneBtn.textContent = step.kind === 'done' ? texts.done : texts.next;
    doneBtn.setAttribute('aria-label', step.kind === 'surface'
      ? fillText(texts.nextTo, { name: step.name })
      : step.kind === 'options' ? fillText(texts.nextTo, { name: productOptions?.title ?? texts.optionsTitle }) : texts.done);
    placePill();
  }

  /**
   * The "Bezbedna zona" pill sits ON the safe area's top edge, the way Zazzle
   * labels the green ring — not floating at the top of the stage.
   *
   * The canvas draws that ring as `.pzd-safe` inside `.pzd-zone`, so the pill
   * reads those two boxes rather than recomputing the zone's geometry: one
   * source of truth for where the line is, whatever the zoom or the pan.
   */
  function placePill(): void {
    if (destroyed) return;
    const safe = options.canvas.element.querySelector<HTMLElement>('.pzd-safe');
    // An empty zone already says what the line is for ("Sve što dodaš ostaje
    // unutar isprekidane linije"), and the pill would sit on that very sentence.
    const emptyZone = ctx.store.value().layers.length === 0;
    safePill.hidden = !safe || emptyZone || !ctx.view.guidelines || ctx.view.review || ctx.config.safeMarginMm <= 0;
    if (!safe) return;
    // Attach to the actual safe rectangle, so opening a taller property panel
    // cannot leave this label floating over the middle of the artwork.
    safe.append(safePill);
    safePill.style.left = '50%'; safePill.style.top = '0';
    safePill.style.transform = 'translate(-50%, -50%)';
  }

  // ── The environment every phone part is handed ─────────────────────────────
  const env: PhoneEnv = {
    ctx,
    texts,
    actions,
    defs: options.defs,
    surfaces,
    ...(options.switchSurface ? { switchSurface: options.switchSurface } : {}),
    root,
    openAdd(tab?: PhoneTabId) {
      closeSheet();
      closeSub(false);
      sheet = openAddDrawer(env, tab);
    },
    openReview() {
      closeSheet();
      sheet = openReviewSheet(env);
    },
    openMore() {
      closeSheet();
      sheet = openMoreSheet(env);
    },
    openSides() {
      closeSheet();
      sheet = openSidesSheet(env);
    },
    ...(productOptions
      ? {
        openOptions() {
          closeSheet();
          closeSub(false);
          optionsSeen = true;
          sheet = openSheet({
            root,
            title: productOptions.title ?? texts.optionsTitle,
            closeLabel: texts.close,
            tall: true,
            initialFocus: 'dialog',
            build: (body) => {
              const slot = el('div', 'pzd-options-slot');
              body.append(slot);
              let unmount: (() => void) | undefined;
              try {
                unmount = productOptions.mount(slot);
              } catch {
                unmount = undefined;
              }
              return () => {
                try {
                  unmount?.();
                } catch {
                  /* the host's controls are the host's to put back */
                }
              };
            },
            onClose: () => syncTop(),
          });
          syncTop();
        },
      }
      : {}),
    openContext() {
      closeSheet();
      sheet = openContextSheet(env);
    },
    showSub,
    refreshGrid,
    syncTop,
    closeSheet,
  };

  // ── The store drives the chrome ────────────────────────────────────────────
  const stopStore = ctx.store.subscribe((state, previous) => {
    syncTop();
    if (state.answer.layers.some(layer => !previous.answer.layers.some(old => old.id === layer.id))) closeSheet();
    if (state.selectedId !== previous.selectedId) {
      // A different object means a different panel; an open one is abandoned
      // (not cancelled — the edits it already made are the shopper's).
      if (sub) closeSub(false);
      refreshGrid();
      return;
    }
    if (state.answer === previous.answer) return;
    if (gridSignature() !== signature) refreshGrid();
  });
  const stopSaved = actions.onSaved(() => {
    savedTag.textContent = texts.saved;
  });

  // ── Long press and right-click open the context sheet ──────────────────────
  let pressTimer: number | undefined;
  let pressAt: { x: number; y: number } | undefined;

  function cancelPress(): void {
    if (pressTimer !== undefined) window.clearTimeout(pressTimer);
    pressTimer = undefined;
    pressAt = undefined;
  }

  function onPressDown(event: PointerEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.pzd-zone')) return;
    if (event.button !== undefined && event.button > 0) return;
    cancelPress();
    pressAt = { x: event.clientX, y: event.clientY };
    pressTimer = window.setTimeout(() => {
      pressTimer = undefined;
      pressAt = undefined;
      suppressOpenUntil = Date.now() + SUPPRESS_MS;
      env.openContext();
    }, PRESS_MS);
  }

  function onPressMove(event: PointerEvent): void {
    if (!pressAt) return;
    if (Math.hypot(event.clientX - pressAt.x, event.clientY - pressAt.y) > PRESS_SLOP) cancelPress();
  }

  function onContextMenu(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.pzd-zone')) return;
    event.preventDefault();
    cancelPress();
    suppressOpenUntil = Date.now() + SUPPRESS_MS;
    env.openContext();
  }

  const canvasEl = options.canvas.element;
  canvasEl.addEventListener('pointerdown', onPressDown, true);
  canvasEl.addEventListener('pointermove', onPressMove, true);
  canvasEl.addEventListener('pointerup', cancelPress, true);
  canvasEl.addEventListener('pointercancel', cancelPress, true);
  canvasEl.addEventListener('contextmenu', onContextMenu);

  // ── Size and keyboard ──────────────────────────────────────────────────────
  function measure(): void {
    const width = root.clientWidth || ctx.root.clientWidth || 0;
    const height = root.clientHeight || ctx.root.clientHeight || 0;
    // The bottom area is capped against this, never against a percentage: a
    // percentage max-height on a grid item resolves against the very track the
    // item is sizing, and the area would cap itself at half of itself.
    if (height > 0) root.style.setProperty('--pzd-ph-h', `${Math.round(height)}px`);
    // The safe-area pill sits on the zone's top edge; the canvas's ruler owns
    // the first 18 px of the stage when it is on.
    root.style.setProperty('--pzd-ph-pill-top', ctx.view.rulers ? '24px' : '8px');
    root.classList.toggle('is-wide', width >= WIDE_AT);
    // A short phone gives the canvas more of what is left: the grid scrolls,
    // the design does not.
    root.classList.toggle('is-short', height > 0 && height < SHORT_SHELL);
    root.classList.toggle(
      'is-landscape',
      height > 0 && height < SHORT_AT && width >= LANDSCAPE_FROM && width > height,
    );
    // A new box means the zone has to be re-fitted into it. The engine asks
    // for that too, but the canvas's own observer is on the rendering
    // lifecycle, and a shell that only re-measures itself would leave a
    // postage-stamp zone in a stage that grew.
    // Wide enough for one row, or the side name moves under the bar.
    if (surfaces.length > 1) {
      const ownRow = width > 0 && width < SIDE_ROW_BELOW;
      sideRow.hidden = !ownRow;
      if (ownRow && sideBtn.parentElement !== sideRow) sideRow.append(sideBtn);
      if (!ownRow && sideBtn.parentElement !== topMid) topMid.insertBefore(sideBtn, problemChip);
    }
    const size = `${width}x${height}`;
    if (size === lastSize) return;
    lastSize = size;
    if (width > 0 && height > 0) options.canvas.relayout();
    placePill();
    window.setTimeout(placePill, 120);
  }

  function onViewport(): void {
    const view = window.visualViewport;
    if (!view) return;
    const keyboard = Math.max(0, window.innerHeight - view.height - view.offsetTop);
    root.classList.toggle('has-keyboard', keyboard > KEYBOARD_AT);
  }
  window.visualViewport?.addEventListener('resize', onViewport);
  window.visualViewport?.addEventListener('scroll', onViewport);
  // The engine calls `relayout`, but a shell that re-measures itself is right
  // even when it is mounted into a host that grows later (a theme column, a
  // rotated tablet, a dialog that animates open).
  //
  // ResizeObserver alone is not enough: it is delivered on the rendering
  // lifecycle, which a hidden or minimised document does not run (the same
  // trap that stops requestAnimationFrame — see index.ts). The window's own
  // events do fire, so they are kept as a second path.
  const stopSize = observeSize(root, measure, 90);
  let resizeTimer: number | undefined;
  const onWindowResize = (): void => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(measure, 80);
  };
  window.addEventListener('resize', onWindowResize);
  window.addEventListener('orientationchange', onWindowResize);

  measure();
  refreshGrid();
  syncTop();
  // The canvas fits the zone from its own ResizeObserver, so the pill is
  // placed again once that has happened.
  const settle = window.setTimeout(placePill, 120);

  return {
    layout: 'phone',
    element: root,
    open(tool: ToolId) {
      // The canvas fires this after a double tap and after ITS long press; the
      // second one belongs to the context sheet we already opened.
      if (Date.now() < suppressOpenUntil) return;
      const layer = ctx.selected();
      if (tool === 'text' && layer?.kind === 'text') {
        runGridItem(env, 'edit');
        openTool = 'text';
        return;
      }
      if (tool === 'image' && layer?.kind === 'image') {
        runGridItem(env, 'crop'); openTool = tool; return;
      }
      env.openAdd(tabForTool(tool));
      openTool = tool;
    },
    close() {
      closeSheet();
      closeSub(false);
    },
    current: () => openTool,
    refresh() {
      syncTop();
      if (!sub) refreshGrid();
    },
    sync() {
      syncTop();
    },
    relayout() {
      measure();
      onViewport();
      options.canvas.relayout();
      placePill();
      window.setTimeout(placePill, 120);
    },
    destroy() {
      destroyed = true;
      cancelPress();
      closeSheet();
      closeSub(false);
      stopStore();
      stopSaved();
      arrange.destroy();
      stopSize();
      window.clearTimeout(resizeTimer);
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener('orientationchange', onWindowResize);
      canvasEl.removeEventListener('pointerdown', onPressDown, true);
      canvasEl.removeEventListener('pointermove', onPressMove, true);
      canvasEl.removeEventListener('pointerup', cancelPress, true);
      canvasEl.removeEventListener('pointercancel', cancelPress, true);
      canvasEl.removeEventListener('contextmenu', onContextMenu);
      window.visualViewport?.removeEventListener('resize', onViewport);
      window.visualViewport?.removeEventListener('scroll', onViewport);
      window.clearTimeout(settle);
      // Give the shopper their desktop furniture back on a layout flip.
      ctx.setView(restoreView);
      ctx.root.classList.remove('is-phone');
      safePill.remove();
      root.remove();
    },
  };
};
